import React, { useState, useEffect, useRef } from 'react';
import watch from 'redux-watch';
import { wrapFunctionalComponent } from 'utils/framework';
import store from 'store/Store';
import MediaUtils from 'utils/Media';
import Empty from 'constants/empty';
import {
    Box, Flex, Link, Text, Divider
} from 'components/ui';
import localeUtils from 'utils/LanguageLocale';
import SmsOptInModal from 'components/SmsOptInModal/SmsOptInModal';
import bccEmailSMSOptInService from 'services/api/bccEmailSmsOptIn';

import PromotionalTextSMSOptInEN from 'components/RichProfile/MyAccount/MailingPrefs/PromotionalTextSMSOptIn/locales/PromotionalTextSMSOptIn_en_US';
import PromotionalTextSMSOptInFR from 'components/RichProfile/MyAccount/MailingPrefs/PromotionalTextSMSOptIn/locales/PromotionalTextSMSOptIn_fr_CA';

const { Media } = MediaUtils;

const getText = localeUtils.getLocalisedDataFromResource(PromotionalTextSMSOptInEN, PromotionalTextSMSOptInFR);

const maskPhone = phone => {
    const cleaned = phone.replace(/\D/g, '');
    const lastFour = cleaned.slice(-4);

    return `(***)***-${lastFour}`;
};

function PromotionalTextSMSOptIn({
    viewMode,
    onExpand,
    onUpdatedPreferences,
    unSubscribedTriggeredFlag,
    subscribedTriggeredFlag,
    onUnsubscribe,
    onSubscribe,
    smsUnsubscribeEnabledFlag
}) {
    const [isSMSOptdInEnabled, setIsSMSOptdInEnabled] = useState(false);
    const [isUserSMSOptedIn, setIsUserSMSOptedIn] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [subscribedTriggered, setSubscribedTriggered] = useState(false);
    const [isUserLoaded, setIsUserLoaded] = useState(false);
    const [confirmedPhone, setConfirmedPhone] = useState(null);
    const innerUnsubscribeRef = useRef(null);
    const [prefs, setPrefs] = useState(null); // The user's current mailing preferences.
    const [primaryPhone, setPrimaryPhone] = useState(null); // The user's primary phone number.
    const rawPhoneRef = useRef('');
    const profileIdRef = useRef(null);

    const smsOptInPageTypes = {
        optIn: 'SMS_optin',
        optOut: 'SMS_optout'
    };

    useEffect(() => {
        const subscribeToMailingPrefs = () => {
            const mailingPrefsWatch = watch(store.getState, 'mailingPreferences');
            innerUnsubscribeRef.current = store.subscribe(
                mailingPrefsWatch(mailingPreferences => {
                    try {
                        setPrefs(mailingPreferences.mailingPreferences);
                    } catch (error) {
                        Sephora.logger.verbose('Error in CommunicationLanguagePrefs', error);
                    }
                })
            );
        };

        const handleUser = watchedUser => {
            if (profileIdRef.current !== watchedUser.profileId) {
                profileIdRef.current = watchedUser.profileId;

                if (innerUnsubscribeRef.current) {
                    innerUnsubscribeRef.current();
                }

                subscribeToMailingPrefs();
            }

            setPrimaryPhone(watchedUser.primaryPhone); // Will be used to handle the un-subscription.
            setIsUserSMSOptedIn(!!watchedUser.isUserSMSOptedIn);
            setIsSMSOptdInEnabled(!!watchedUser.isSMSOptInAvailable);
            setIsUserLoaded(true);
        };

        handleUser(store.getState().user);

        const userWatch = watch(store.getState, 'user');
        const unsubscribeUser = store.subscribe(userWatch(handleUser));

        return () => {
            unsubscribeUser();

            if (innerUnsubscribeRef.current) {
                innerUnsubscribeRef.current();
            }
        };
    }, []);

    useEffect(() => {
        if (viewMode) {
            setIsModalOpen(false);
        }
    }, [viewMode]);

    useEffect(() => {
        setIsUserSMSOptedIn(false);
    }, [unSubscribedTriggeredFlag]);

    useEffect(() => {
        setSubscribedTriggered(!!subscribedTriggeredFlag);
    }, [subscribedTriggeredFlag]);

    useEffect(() => {
        if (confirmedPhone && confirmedPhone !== Empty.String) {
            setSubscribedTriggered(false);
        }
    }, [confirmedPhone]);

    const handleSubscribeClick = () => {
        if (isUserSMSOptedIn) {
            const locale = localeUtils.getCurrentLanguageCountryCode();
            const pageName = digitalData?.page?.pageInfo?.pageName || Empty.String;
            const phoneNumber = rawPhoneRef.current || primaryPhone;

            if (!phoneNumber || phoneNumber === Empty.String) {
                Sephora.logger.error('SMS opt-out aborted: no phone number available for the current user.');

                return;
            }

            callSMSOptInService({ phoneNumber, locale, pageName, pageType: smsOptInPageTypes.optOut });
        } else {
            setIsModalOpen(true);
            onExpand?.();
        }
    };

    const handleModalContinue = phoneNumber => {
        rawPhoneRef.current = phoneNumber;
    };

    const handleModalDismiss = ({ formSubmittedSuccessfully } = {}) => {
        setIsModalOpen(false);

        if (formSubmittedSuccessfully) {
            setConfirmedPhone(maskPhone(rawPhoneRef.current));

            if (onUpdatedPreferences) {
                onUpdatedPreferences();
            }
        }
    };

    const callSMSOptInService = async ({ phoneNumber, locale, pageName, pageType = smsOptInPageTypes.optIn }) => {
        try {
            await bccEmailSMSOptInService.submitEmailSMSOptInForm({
                phoneNumber,
                locale,
                pageName,
                pageType,
                type: 'SMS'
            });

            // Updates the user profile.
            onUpdatedPreferences?.();
        } catch (err) {
            Sephora.logger.error(`Error submitting SMS ${pageType} request: `, { err });
        }
    };

    const handleResend = () => {
        const locale = localeUtils.getCurrentLanguageCountryCode();
        const pageName = digitalData?.page?.pageInfo?.pageName || Empty.String;
        const phoneNumber = rawPhoneRef.current || primaryPhone;
        callSMSOptInService({ phoneNumber, locale, pageName, pageType: smsOptInPageTypes.optIn });
        onSubscribe?.(false);
        onUnsubscribe?.(false);
    };

    const titleCell = (
        <Text
            is='h2'
            fontSize='md'
            fontWeight='bold'
            lineHeight='tight'
        >
            {getText('promotionalTexts')}
        </Text>
    );

    const bodyCell = (
        <>
            <Text is='p'>{getText('description')}</Text>
            <Box marginTop={3}>
                <Text is='p'>
                    <b>{getText('status')}</b> {isUserSMSOptedIn ? getText('subscribed') : getText('notSubscribed')}
                </Text>
            </Box>
        </>
    );

    const actionCell = (
        <Link
            color='blue'
            paddingY={2}
            marginY={-2}
            onClick={handleSubscribeClick}
        >
            {isUserSMSOptedIn ? getText('unsubscribe') : getText('subscribe')}
        </Link>
    );

    const getPhoneNumberToDisplay = () => {
        return subscribedTriggered ? maskPhone(primaryPhone || '') : maskPhone(confirmedPhone || '');
    };

    // When the user has re-subscribed, confirmation banner is shown with the masked phone number and a "resend" link.
    const confirmationBanner = (subscribedTriggered || confirmedPhone) && (
        <Text
            is='p'
            fontSize='sm'
            color='gray'
            lineHeight='tight'
        >
            {getText('confirmationPrefix')}
            {getPhoneNumberToDisplay()}
            {getText('confirmationSuffix')}{' '}
            <Link
                color='blue'
                onClick={handleResend}
            >
                {getText('resend')}
            </Link>
        </Text>
    );

    return smsUnsubscribeEnabledFlag && isSMSOptdInEnabled && isUserLoaded && prefs !== null ? (
        <Box>
            <SmsOptInModal
                isOpen={isModalOpen}
                onDismiss={handleModalDismiss}
                onContinue={handleModalContinue}
                smsTextOptInPromos={[]}
                shouldOpenModal={true}
            />
            <Media lessThan='md'>
                <Flex
                    gap={3}
                    alignItems='baseline'
                    paddingY={5}
                    flexWrap='wrap'
                >
                    <Box flex='1'>{titleCell}</Box>
                    <Box
                        flexShrink={0}
                        textAlign='right'
                        lineHeight='tight'
                    >
                        {actionCell}
                    </Box>
                    <Box
                        width='100%'
                        css={{ order: 2 }}
                    >
                        <Divider marginY={3} />
                        {bodyCell}
                    </Box>
                </Flex>

                {(confirmedPhone || subscribedTriggered) && (
                    <Box
                        paddingX={3}
                        paddingY={2}
                        backgroundColor='nearWhite'
                        borderRadius={2}
                        marginBottom={5}
                    >
                        {confirmationBanner}
                    </Box>
                )}
            </Media>

            <Media greaterThan='sm'>
                <Flex
                    gap={3}
                    alignItems='baseline'
                    paddingY={5}
                >
                    <Box
                        width='16em'
                        flexShrink={0}
                    >
                        {titleCell}
                    </Box>
                    <Box flex='1'>{bodyCell}</Box>
                    <Box
                        width='4em'
                        flexShrink={0}
                        textAlign='right'
                        lineHeight='tight'
                    >
                        {actionCell}
                    </Box>
                </Flex>
                {(confirmedPhone || subscribedTriggered) && (
                    <Flex
                        gap={3}
                        marginBottom={5}
                    >
                        <Box
                            width='16em'
                            flexShrink={0}
                        />
                        <Box flex='1'>
                            <Box
                                paddingX={3}
                                paddingY={2}
                                backgroundColor='nearWhite'
                                borderRadius={2}
                            >
                                {confirmationBanner}
                            </Box>
                        </Box>
                    </Flex>
                )}
            </Media>
        </Box>
    ) : (
        <></>
    );
}

export default wrapFunctionalComponent(PromotionalTextSMSOptIn, 'PromotionalTextSMSOptIn');
